/**
 * Geodesy for coastal navigation.
 *
 * All positions are WGS84 decimal degrees; all distances are metres; all
 * bearings are degrees TRUE (0..360, north = 0, clockwise).
 *
 * Rhumb line vs great circle
 * --------------------------
 * A rhumb line holds a constant compass bearing — it is what you actually steer
 * and what a chartplotter draws for a route leg. A great circle is the shortest
 * path but requires continuous course change. For coastal legs the two differ by
 * far less than steering error, but the distinction matters for long passages,
 * so both are provided and the route model states which it uses.
 */

export interface LatLon {
  lat: number;
  lon: number;
}

/** IUGG mean Earth radius, metres. */
export const EARTH_RADIUS_M = 6371008.8;

/**
 * Latitude change, radians, below which a rhumb line is treated as running along
 * a parallel. See `rhumbDistance` for why this value.
 */
const RHUMB_PARALLEL_THRESHOLD_RAD = 1e-5;

const toRad = (d: number): number => (d * Math.PI) / 180;
const toDeg = (r: number): number => (r * 180) / Math.PI;

/** Wrap a bearing into [0, 360). */
export function normaliseBearing(degrees: number): number {
  return ((degrees % 360) + 360) % 360;
}

/**
 * Wrap a longitude difference into the half-open range [-180, 180).
 * Exactly ±180 both normalise to -180; they are the same meridian, so which
 * representative is returned only matters for consistency, not correctness.
 */
export function normaliseLonDelta(degrees: number): number {
  // Double-modulo rather than a single (+540) offset: the offset form only
  // covers inputs within one revolution and lets values just past -540 escape
  // the range by a rounding error.
  return ((((degrees + 180) % 360) + 360) % 360) - 180;
}

/** True if a position is a finite, in-range WGS84 coordinate. */
export function isValidLatLon(p: unknown): p is LatLon {
  if (typeof p !== 'object' || p === null) return false;
  const { lat, lon } = p as LatLon;
  return (
    Number.isFinite(lat) && Number.isFinite(lon)
    && lat >= -90 && lat <= 90
    && lon >= -180 && lon <= 180
  );
}

/**
 * Great-circle distance (haversine), metres.
 * Numerically stable for the very short distances typical of manoeuvring.
 */
export function greatCircleDistance(a: LatLon, b: LatLon): number {
  const phi1 = toRad(a.lat);
  const phi2 = toRad(b.lat);
  const dPhi = toRad(b.lat - a.lat);
  const dLambda = toRad(normaliseLonDelta(b.lon - a.lon));

  const sinDPhi = Math.sin(dPhi / 2);
  const sinDLambda = Math.sin(dLambda / 2);
  const h = sinDPhi * sinDPhi + Math.cos(phi1) * Math.cos(phi2) * sinDLambda * sinDLambda;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Initial bearing of the great circle from `a` to `b`, degrees true. */
export function greatCircleBearing(a: LatLon, b: LatLon): number {
  const phi1 = toRad(a.lat);
  const phi2 = toRad(b.lat);
  const dLambda = toRad(normaliseLonDelta(b.lon - a.lon));

  const y = Math.sin(dLambda) * Math.cos(phi2);
  const x = Math.cos(phi1) * Math.sin(phi2) - Math.sin(phi1) * Math.cos(phi2) * Math.cos(dLambda);
  return normaliseBearing(toDeg(Math.atan2(y, x)));
}

/**
 * Rhumb-line (loxodromic) distance, metres — the distance run at a constant
 * bearing. Uses the inverse Gudermannian ("stretched latitude") formulation.
 */
export function rhumbDistance(a: LatLon, b: LatLon): number {
  const phi1 = toRad(a.lat);
  const phi2 = toRad(b.lat);
  const dPhi = phi2 - phi1;
  let dLambda = toRad(normaliseLonDelta(b.lon - a.lon));

  const dPsi = Math.log(
    Math.tan(Math.PI / 4 + phi2 / 2) / Math.tan(Math.PI / 4 + phi1 / 2),
  );
  // q is the east-west scaling: the mean of cos(latitude) over the leg.
  //
  // dPsi is a log of a ratio near 1, so it loses precision catastrophically as
  // the leg approaches a parallel — by dPhi ~ 1e-8 rad the ratio dPhi/dPsi is
  // meaningless. Below the threshold the analytic limit cos(latitude) is used
  // instead, evaluated at the MEAN latitude so that the result is identical in
  // both directions; an asymmetric distance would make a route measure
  // differently once reversed.
  //
  // 1e-5 rad (about 64 m of latitude) sits comfortably between the two error
  // regimes: cancellation error below it is under 1e-11 relative, and the
  // O(dPhi^2) error of the cos(mean) approximation above it is under 1e-10.
  const q = Math.abs(dPhi) > RHUMB_PARALLEL_THRESHOLD_RAD
    ? dPhi / dPsi
    : Math.cos((phi1 + phi2) / 2);

  // Take the shorter way round the globe.
  if (Math.abs(dLambda) > Math.PI) {
    dLambda = dLambda > 0 ? dLambda - 2 * Math.PI : dLambda + 2 * Math.PI;
  }
  return Math.hypot(dPhi, q * dLambda) * EARTH_RADIUS_M;
}

/** Constant bearing from `a` to `b`, degrees true. */
export function rhumbBearing(a: LatLon, b: LatLon): number {
  const phi1 = toRad(a.lat);
  const phi2 = toRad(b.lat);
  let dLambda = toRad(normaliseLonDelta(b.lon - a.lon));

  const dPsi = Math.log(
    Math.tan(Math.PI / 4 + phi2 / 2) / Math.tan(Math.PI / 4 + phi1 / 2),
  );
  if (Math.abs(dLambda) > Math.PI) {
    dLambda = dLambda > 0 ? dLambda - 2 * Math.PI : dLambda + 2 * Math.PI;
  }
  return normaliseBearing(toDeg(Math.atan2(dLambda, dPsi)));
}

/** Position reached by running `distance` metres from `start` on a great circle. */
export function destinationPoint(start: LatLon, bearingDeg: number, distanceM: number): LatLon {
  const delta = distanceM / EARTH_RADIUS_M;
  const theta = toRad(bearingDeg);
  const phi1 = toRad(start.lat);
  const lambda1 = toRad(start.lon);

  const sinPhi2 = Math.sin(phi1) * Math.cos(delta) + Math.cos(phi1) * Math.sin(delta) * Math.cos(theta);
  const phi2 = Math.asin(Math.min(1, Math.max(-1, sinPhi2)));
  const lambda2 = lambda1 + Math.atan2(
    Math.sin(theta) * Math.sin(delta) * Math.cos(phi1),
    Math.cos(delta) - Math.sin(phi1) * sinPhi2,
  );

  return { lat: toDeg(phi2), lon: normaliseLonDelta(toDeg(lambda2)) };
}

/**
 * Signed cross-track distance of `point` from the great circle through
 * `start`->`end`, metres. Positive means the point lies to starboard of the
 * track (right of the intended course). This is the XTE a helm steers to zero.
 */
export function crossTrackDistance(point: LatLon, start: LatLon, end: LatLon): number {
  const d13 = greatCircleDistance(start, point) / EARTH_RADIUS_M;
  if (d13 === 0) return 0;
  const theta13 = toRad(greatCircleBearing(start, point));
  const theta12 = toRad(greatCircleBearing(start, end));
  return Math.asin(Math.min(1, Math.max(-1, Math.sin(d13) * Math.sin(theta13 - theta12)))) * EARTH_RADIUS_M;
}

/**
 * Distance from `start` along the track towards `end` at which `point` lies
 * abeam, metres. Negative means the point is behind the start of the leg.
 */
export function alongTrackDistance(point: LatLon, start: LatLon, end: LatLon): number {
  const d13 = greatCircleDistance(start, point) / EARTH_RADIUS_M;
  if (d13 === 0) return 0;
  const xt = crossTrackDistance(point, start, end) / EARTH_RADIUS_M;
  const cosRatio = Math.cos(d13) / Math.cos(xt);
  return Math.acos(Math.min(1, Math.max(-1, cosRatio))) * Math.sign(Math.cos(toRad(
    greatCircleBearing(start, point) - greatCircleBearing(start, end),
  ))) * EARTH_RADIUS_M;
}

/**
 * Point on segment `start`->`end` closest to `point`, clamped to the segment
 * ends. Used to snap a proposed waypoint onto an existing leg, and to find where
 * a route passes nearest a charted hazard.
 */
export function nearestPointOnSegment(point: LatLon, start: LatLon, end: LatLon): LatLon {
  const legLength = greatCircleDistance(start, end);
  if (legLength === 0) return { ...start };

  const along = alongTrackDistance(point, start, end);
  if (along <= 0) return { ...start };
  if (along >= legLength) return { ...end };
  return destinationPoint(start, greatCircleBearing(start, end), along);
}

/**
 * Sample positions along a leg at no more than `spacingM` apart, always
 * including both endpoints. Used to test a route against charted depths — a leg
 * is only as safe as the shallowest point under it, so it must be checked at
 * intervals rather than only at its waypoints.
 */
export function sampleLeg(start: LatLon, end: LatLon, spacingM: number): LatLon[] {
  if (!(spacingM > 0)) throw new RangeError('spacingM must be positive');
  const distance = greatCircleDistance(start, end);
  if (distance === 0) return [{ ...start }];

  const steps = Math.ceil(distance / spacingM);
  const bearing = greatCircleBearing(start, end);
  const points: LatLon[] = [];
  for (let i = 0; i < steps; i++) {
    points.push(destinationPoint(start, bearing, (distance * i) / steps));
  }
  points.push({ ...end });
  return points;
}

/** Bounding box of a set of positions, or null when the set is empty. */
export function boundsOf(points: readonly LatLon[]): { south: number; west: number; north: number; east: number } | null {
  if (points.length === 0) return null;
  let south = Infinity; let west = Infinity; let north = -Infinity; let east = -Infinity;
  for (const p of points) {
    if (p.lat < south) south = p.lat;
    if (p.lat > north) north = p.lat;
    if (p.lon < west) west = p.lon;
    if (p.lon > east) east = p.lon;
  }
  return { south, west, north, east };
}
